/**
 * Timezone helpers, mirroring src/shared/utils/timezone.utils.ts in the backend.
 *
 * A tournament's scheduling constraints (start date, playable days, daily start
 * and end times) are wall-clock intent in the tournament's own zone, while match
 * start times come back from the API as absolute instants. Formatting an instant
 * with the browser's zone - or building one from a date/time input the same way -
 * shows or saves the wrong local day and hour for anyone whose browser is not in
 * the tournament's zone.
 *
 * A "wall clock" here is a Date whose UTC fields hold the local reading: 22:00 in
 * Cairo is 2026-09-15T22:00:00Z as a wall clock and 2026-09-15T19:00:00Z as an
 * instant.
 */

export const DEFAULT_TIMEZONE = "Africa/Cairo";

const formatterCache = new Map<string, Intl.DateTimeFormat>();

const getPartsFormatter = (timeZone: string): Intl.DateTimeFormat => {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
};

/** True when `timeZone` is an IANA zone this browser understands. */
export const isValidTimeZone = (timeZone: string | null | undefined): boolean => {
  if (!timeZone) return false;
  try {
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
};

/** A usable IANA zone, falling back to {@link DEFAULT_TIMEZONE}. */
export const resolveTimeZone = (timeZone: string | null | undefined): string =>
  isValidTimeZone(timeZone) ? (timeZone as string) : DEFAULT_TIMEZONE;

/** Build a wall clock from local calendar fields. `month` is 1-12. */
export const makeWallClock = (
  year: number,
  month: number,
  day: number,
  hours = 0,
  minutes = 0,
  seconds = 0
): Date => new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds, 0));

/** Read an absolute instant as the wall clock an observer in `timeZone` sees. */
export const toWallClock = (instant: Date, timeZone: string): Date => {
  const parts = getPartsFormatter(timeZone).formatToParts(instant);
  const field = (type: string): number => {
    const part = parts.find((p) => p.type === type);
    return part ? Number(part.value) : 0;
  };
  return makeWallClock(
    field("year"),
    field("month"),
    field("day"),
    field("hour"),
    field("minute"),
    field("second")
  );
};

/** Minutes to add to a UTC instant to get the local reading in `timeZone`. */
export const getTimeZoneOffsetMinutes = (instant: Date, timeZone: string): number =>
  (toWallClock(instant, timeZone).getTime() - instant.getTime()) / 60000;

/**
 * Resolve a wall clock in `timeZone` to the absolute instant it refers to.
 *
 * Applied twice on purpose: the first pass uses the offset in force at the naive
 * guess, the second re-checks it at the resulting instant so wall clocks near a
 * DST transition land on the offset that actually applies to them.
 */
export const fromWallClock = (wallClock: Date, timeZone: string): Date => {
  const target = wallClock.getTime();
  const firstGuess = new Date(target - getTimeZoneOffsetMinutes(new Date(target), timeZone) * 60000);
  return new Date(target - getTimeZoneOffsetMinutes(firstGuess, timeZone) * 60000);
};

/**
 * Turn `<input type="date">` + `<input type="time">` values into the instant they
 * name in `timeZone`. This is the write path: reading them with `new Date(...)`
 * would interpret them in the browser's zone instead.
 */
export const dateTimeInputsToInstant = (
  dateValue: string,
  timeValue: string,
  timeZone: string
): Date => {
  const [year, month, day] = dateValue.split("-").map(Number);
  const [hours, minutes] = timeValue.split(":").map(Number);
  return fromWallClock(
    makeWallClock(year, month, day, hours || 0, minutes || 0),
    resolveTimeZone(timeZone)
  );
};

/** The `YYYY-MM-DD` value an `<input type="date">` needs for this instant. */
export const toDateInputValue = (instant: Date | string, timeZone: string): string => {
  const date = typeof instant === "string" ? new Date(instant) : instant;
  if (isNaN(date.getTime())) return "";
  return toWallClock(date, resolveTimeZone(timeZone)).toISOString().split("T")[0];
};

/** The `HH:MM` value an `<input type="time">` needs for this instant. */
export const toTimeInputValue = (instant: Date | string, timeZone: string): string => {
  const date = typeof instant === "string" ? new Date(instant) : instant;
  if (isNaN(date.getTime())) return "";
  const wall = toWallClock(date, resolveTimeZone(timeZone));
  return `${String(wall.getUTCHours()).padStart(2, "0")}:${String(wall.getUTCMinutes()).padStart(2, "0")}`;
};

/**
 * A stable `YYYY-MM-DD` key for the local day an instant falls on, for grouping
 * matches into calendar days.
 */
export const toDayKey = (instant: Date | string, timeZone: string): string =>
  toDateInputValue(instant, timeZone);

/** Format an instant in the tournament's zone rather than the browser's. */
export const formatInTimeZone = (
  instant: Date | string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
  locale = "en-GB"
): string => {
  const date = typeof instant === "string" ? new Date(instant) : instant;
  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: resolveTimeZone(timeZone) }).format(date);
};

/** The short zone label to show beside a time, e.g. "GMT+3". */
export const getTimeZoneLabel = (timeZone: string, instant: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: resolveTimeZone(timeZone),
    timeZoneName: "shortOffset",
  }).formatToParts(instant);
  const zonePart = parts.find((p) => p.type === "timeZoneName");
  return zonePart ? zonePart.value : "";
};

/**
 * IANA zones offered in the tournament settings. The browser's own zone is added
 * at runtime when it is not already here, so an organiser elsewhere is not stuck
 * picking from a list that excludes them.
 */
const BASE_TIMEZONE_OPTIONS = [
  "Africa/Cairo",
  "Africa/Casablanca",
  "Africa/Lagos",
  "Africa/Nairobi",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Qatar",
  "Asia/Kuwait",
  "Asia/Amman",
  "Asia/Beirut",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Istanbul",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "UTC",
];

export const getTimeZoneOptions = (current?: string | null): string[] => {
  const options = [...BASE_TIMEZONE_OPTIONS];
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  [browserZone, current].forEach((zone) => {
    if (zone && isValidTimeZone(zone) && !options.includes(zone)) {
      options.push(zone);
    }
  });

  return options;
};
